import dayjs from 'dayjs';
import type { RiskSegmentConfig, RiskThresholdConfig } from './types';

export function formatAmountCurrency(value?: number): string {
  if (typeof value !== 'number' || Number.isNaN(value)) return '¥ ——';

  const absoluteValue = Math.abs(value);
  const displayValue =
    absoluteValue >= 10000
      ? Number((value / 10000).toFixed(2))
      : Number(value.toFixed(2));
  const normalizedText = `${displayValue}`
    .replace(/(\.\d*?[1-9])0+$/, '$1')
    .replace(/\.0+$/, '');
  const suffix = absoluteValue >= 10000 ? ' 万' : '';

  return `¥ ${normalizedText}${suffix}`;
}

export function formatMonthValue(months: number): string {
  if (!Number.isFinite(months)) return '∞';
  return months.toFixed(1).replace(/\.0$/, '');
}

export function formatRiskCoverageMonthsFromDays(days?: number): string {
  if (typeof days !== 'number' || Number.isNaN(days)) return '0';

  const truncatedMonths = Math.floor((days / 30) * 10) / 10;
  return truncatedMonths.toFixed(1).replace(/\.0$/, '');
}

export function getRiskCoverageDescription(
  days?: number,
  referenceDate?: string
): string {
  if (typeof days !== 'number' || Number.isNaN(days)) {
    return '待预测：可覆盖月数 / 覆盖期终点';
  }

  if (days >= 720) {
    return '可覆盖未来24个月以上的刚性支出';
  }

  return `可覆盖未来 ${formatRiskCoverageMonthsFromDays(days)} 个月刚性支出 · 至 ${formatDisplayDate(referenceDate || '')}`;
}

export function formatDisplayDate(
  value: string,
  pattern = 'YYYY-MM-DD'
): string {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format(pattern) : value;
}

export function getRiskSegment(
  config: RiskThresholdConfig,
  months: number
): RiskSegmentConfig {
  const matched = config.segments.find(
    segment => months >= segment.lowerBoundMonths
  );
  return matched || config.segments[config.segments.length - 1];
}

export function getRiskPointerPercent(
  config: RiskThresholdConfig,
  months: number
): number {
  const { breakpoints } = config;
  const segmentCount = breakpoints.length - 1;

  if (segmentCount <= 0) return 0;
  if (months >= breakpoints[0]) return 0;
  if (months <= breakpoints[segmentCount]) return 100;

  for (let index = 0; index < segmentCount; index += 1) {
    const left = breakpoints[index];
    const right = breakpoints[index + 1];

    if (months <= left && months >= right) {
      const segmentStart = (index / segmentCount) * 100;
      const segmentWidth = 100 / segmentCount;
      const ratio = (left - months) / (left - right);
      return Number((segmentStart + segmentWidth * ratio).toFixed(2));
    }
  }

  return 100;
}

export function getQuickRangeLabel(days: number): string {
  const presetMap: Record<number, string> = {
    30: '1 月',
    90: '3 月',
    180: '6 月',
  };

  return presetMap[days] || `${Math.max(Math.round(days / 30), 1)} 月`;
}
